import React, { useCallback } from 'react';
import { FlatList, StyleSheet } from 'react-native';
import { SharedValue } from 'react-native-reanimated';

import { useIsFocused } from '@react-navigation/native';

import { Box } from '@/components/lib/components';

import { GameWithMetadata, useArcadeGames } from '../hooks/use-arcade-games';
import { useVisibleArcadeRows } from '../hooks/use-visible-arcade-rows';
import { ArcadeGameCard } from './ArcadeGameCard';

const NUM_COLUMNS = 2;

const keyExtractor = (item: GameWithMetadata) => item.game_id;

type ArcadeGamesListProps = {
    scrollOffset: SharedValue<number>;
};

export const ArcadeGamesList = ({ scrollOffset }: ArcadeGamesListProps) => {
    const { data, isLoading } = useArcadeGames();
    const isFocused = useIsFocused();
    const gameCount = data?.length ?? 0;
    const isOdd = gameCount % NUM_COLUMNS === 1;

    const { isRowVisible, onListLayout, onFirstCellLayout } = useVisibleArcadeRows(scrollOffset);

    const renderItem = useCallback(
        ({ item, index }: { item: GameWithMetadata; index: number }) => {
            const isLastItem = index === gameCount - 1;
            const row = Math.floor(index / NUM_COLUMNS);
            const shouldPlayVideo = isFocused && isRowVisible(row);
            return (
                <Box
                    mr={isLastItem && isOdd ? 's12' : 's0'}
                    flex={1 / NUM_COLUMNS}
                    onLayout={index === 0 ? onFirstCellLayout : undefined}
                >
                    <ArcadeGameCard game={item} shouldPlayVideo={shouldPlayVideo} />
                </Box>
            );
        },
        [gameCount, isOdd, isFocused, isRowVisible, onFirstCellLayout]
    );

    if (isLoading || !data) {
        return null;
    }

    return (
        <Box px="s16" onLayout={onListLayout}>
            <FlatList
                data={data}
                keyExtractor={keyExtractor}
                renderItem={renderItem}
                numColumns={NUM_COLUMNS}
                scrollEnabled={false}
                showsVerticalScrollIndicator={false}
                columnWrapperStyle={styles.columnWrapper}
                contentContainerStyle={styles.contentContainer}
            />
        </Box>
    );
};

const styles = StyleSheet.create({
    contentContainer: {
        paddingBottom: 40,
    },
    columnWrapper: {
        gap: 16,
    },
});
